import { useState } from 'react';
import {
  STATUS_CATEGORIES,
  STATUS_COLORS,
  type StatusCategory,
  type TaskPriority,
  type TaskStatus,
} from '@forecastify/shared';
import { Button, Dialog, Select, Tabs, TextInput, type MenuItem } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { ColorPicker } from '../delivery/color-picker';
import { Field } from '../card';
import { useDragOrder } from './settings-shell';
import { SettingsPage } from './settings-page';
import { SettingsTable, type SettingsRow } from './settings-table';
import { WeightsPanel } from './weights-panel';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Taskok — statuses and priorities. `markup.html` 1892–1955.                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Two cards, each a reorderable list with a row menu, a default badge and a task count.
 *
 * ⚠️⚠️ **THE API IS C8'S AND WAS ALREADY COMPLETE — this is a screen, not a feature.** C8 shipped
 * `VocabularyService` with create, update, set-default, reorder and delete-with-reassign for both,
 * plus `/task-statuses` and `/task-priorities`; `chunks.md` handed the *admin* to C16. Nothing here
 * invents an endpoint.
 *
 * ⚠️⚠️ **REORDERING IS BUTTONS, NOT DRAG-AND-DROP, AND THAT IS A DELIBERATE DIVERGENCE.** The mockup
 * uses HTML5 drag events (`dragStart` / `dragOver` / `drop`). Two reasons against porting them: a
 * drag has **no keyboard equivalent** — `draggable` is invisible to a keyboard and to a screen reader,
 * so the only way to reorder would be with a mouse — and the design system has no drag-reorder
 * component, which its own gap list records (*"drag-reorder rows"*). Up/down buttons send the same
 * whole-list `reorder` request, work for everybody, and are assertable in a test. Recorded in
 * `chunks.md` as a divergence rather than papered over.
 */

export interface VocabularySectionProps {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly statuses: readonly TaskStatus[];
  readonly priorities: readonly TaskPriority[];
  readonly canManage: boolean;
  readonly busy: boolean;
  readonly serverError: string | null;

  readonly onCreateStatus: (body: {
    name: string;
    color: string;
    category: StatusCategory;
  }) => Promise<boolean>;
  readonly onUpdateStatus: (
    id: string,
    body: {
      name: string;
      color: string;
      category: StatusCategory;
      version: number;
    },
  ) => Promise<boolean>;
  readonly onDefaultStatus: (id: string) => void;
  readonly onReorderStatuses: (ids: readonly string[]) => void;
  readonly onDeleteStatus: (id: string, reassignToId: string) => void;

  readonly onCreatePriority: (body: { name: string; color: string }) => Promise<boolean>;
  readonly onUpdatePriority: (
    id: string,
    body: { name: string; color: string; version: number },
  ) => Promise<boolean>;
  readonly onDefaultPriority: (id: string) => void;
  readonly onReorderPriorities: (ids: readonly string[]) => void;
  readonly onDeletePriority: (id: string, reassignToId: string) => void;
}

/**
 * ⚠⚠ **EXHAUSTIVE ON PURPOSE, BECAUSE THE FALLTHROUGH VERSION WAS A TRAP.** This read
 * `if open … if in_progress … return closed`, so when `0141` added a fourth kind every status
 * filed under it would have rendered as *Lezárt* — a wrong label with no error, on the one screen
 * where somebody is choosing the kind. The `never` makes the compiler the thing that notices next
 * time instead of a reader.
 */
export function categoryLabel(t: Translate, category: StatusCategory): string {
  /* One `t()` per branch with a literal key. */
  switch (category) {
    case 'open':
      return t('status.category.open');
    case 'in_progress':
      return t('status.category.inProgress');
    case 'in_review':
      return t('status.category.inReview');
    case 'closed':
      return t('status.category.closed');
    default: {
      const exhaustive: never = category;
      return exhaustive;
    }
  }
}

type Editing =
  | { kind: 'status'; row: TaskStatus | null }
  | { kind: 'priority'; row: TaskPriority | null }
  | null;

type Deleting = { kind: 'status' | 'priority'; id: string; name: string; count: number } | null;

export function VocabularySection(props: VocabularySectionProps) {
  const t = useT();
  /* 26 Oct 2026 · a save in flight, and whether the last one was refused. */
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const { statuses, priorities, canManage } = props;
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ TWO TABS, WHERE THIS WAS TWO CARDS STACKED — 15 Sep.                 │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * *"It needs two tabs, one for statuses, then the other for priorities, and they should have
   * purple CTA buttons inline with the tab selector for each."*
   *
   * ⚠️ **Stacking them made the second one invisible.** Statuses is the longer list — a workspace
   * has five or six, each with a category and a colour — so priorities sat below a
   * screen's worth of rows and was found by scrolling past something else. Tabs put them at the
   * same depth, which is what they are.
   *
   * ⚠️ **The counts ride on the tabs**, as they do on Work schedules and Leave: a tab that says how
   * many is a tab somebody can decide about without opening it.
   */
  /* 30 Oct 2026 · EV-2 · a third tab, the status categories' weights. */
  const [tab, setTab] = useState<'statuses' | 'priorities' | 'weights'>('statuses');

  /**
   * ⚠️ **A grabber, not chevrons — 15 Sep.** `useDragOrder` is the one implementation the leave
   * types, the overtime types and these two now share; it also returns `moveTo`, because no key
   * event starts an HTML5 drag and the row menu is the keyboard path. The chevrons this replaces
   * existed for that reason and were right about it.
   */
  const statusOrder = useDragOrder(
    statuses.map((row) => row.id),
    canManage && !props.busy ? props.onReorderStatuses : undefined,
  );
  const priorityOrder = useDragOrder(
    priorities.map((row) => row.id),
    canManage && !props.busy ? props.onReorderPriorities : undefined,
  );

  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<Deleting>(null);

  /* ⚠️ ONE query across both tabs, cleared by neither: somebody searching *review* and switching
     tabs is looking for the same word in the other vocabulary. */
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');

  const matches = (name: string): boolean =>
    query.trim() === '' || name.toLowerCase().includes(query.trim().toLowerCase());

  /**
   * ⚠️⚠️ **THE GRABBER IS BACK, BECAUSE THE ORDER IS THE SETTING.** It went when these rows moved
   * to `DataList`, which has no draggable row — and the order here is the Kanban column order and
   * the priority ranking, so losing it cost something real. The settings table carries a `drag`
   * cell precisely so it did not have to stay lost.
   */
  const statusRows: SettingsRow[] = statuses
    .filter((one) => matches(one.name) && (category === '' || one.category === category))
    .map((status, index) => ({
      id: status.id,
      ...statusOrder.rowProps(status.id),
      draggable: statusOrder.enabled,
      dragging: statusOrder.dragging === status.id,
      over: statusOrder.over === status.id,
      onOpen: !canManage
        ? undefined
        : () => {
            setEditing({ kind: 'status', row: status });
          },
      cells: [
        statusOrder.enabled ? { kind: 'drag' as const } : { kind: 'blank' as const },
        { kind: 'name' as const, text: status.name, color: status.color },
        {
          kind: 'badge' as const,
          text: categoryLabel(t, status.category),
          tone: 'secondary' as const,
        },
        status.isDefault
          ? { kind: 'badge' as const, text: t('settings.default'), tone: 'outline' as const }
          : { kind: 'blank' as const },
        /* ⚠️ A reader who cannot manage gets no menu at all — a `blank` keeps the grid honest
           rather than leaving a control that refuses everything it offers. */
        canManage
          ? {
              kind: 'kebab' as const,
              ariaLabel: t('settings.rowMenu', { name: status.name }),
              items: statusMenu(status.id, index),
            }
          : { kind: 'blank' as const },
      ],
    }));

  const priorityRows: SettingsRow[] = priorities
    .filter((one) => matches(one.name))
    .map((priority, index) => ({
      id: priority.id,
      ...priorityOrder.rowProps(priority.id),
      draggable: priorityOrder.enabled,
      dragging: priorityOrder.dragging === priority.id,
      over: priorityOrder.over === priority.id,
      onOpen: !canManage
        ? undefined
        : () => {
            setEditing({ kind: 'priority', row: priority });
          },
      cells: [
        priorityOrder.enabled ? { kind: 'drag' as const } : { kind: 'blank' as const },
        { kind: 'name' as const, text: priority.name, color: priority.color },
        priority.isDefault
          ? { kind: 'badge' as const, text: t('settings.default'), tone: 'outline' as const }
          : { kind: 'blank' as const },
        /* ⚠️ A reader who cannot manage gets no menu at all — a `blank` keeps the grid honest
           rather than leaving a control that refuses everything it offers. */
        canManage
          ? {
              kind: 'kebab' as const,
              ariaLabel: t('settings.rowMenu', { name: priority.name }),
              items: priorityMenu(priority.id, index),
            }
          : { kind: 'blank' as const },
      ],
    }));

  function statusMenu(id: string, index: number): MenuItem[] {
    const status = statuses[index];
    if (status === undefined) return [];
    return rowMenu(t, {
      name: status.name,
      index,
      total: statuses.length,
      onMove: (to) => {
        statusOrder.moveTo(status.id, to);
      },
      isDefault: status.isDefault,
      canDelete: statuses.length > 1,
      onEdit: () => {
        setEditing({ kind: 'status', row: status });
      },
      onDefault: () => {
        props.onDefaultStatus(status.id);
      },
      onDelete: () => {
        setDeleting({
          kind: 'status',
          id: status.id,
          name: status.name,
          count: status.taskCount,
        });
      },
    });
  }

  function priorityMenu(id: string, index: number): MenuItem[] {
    const priority = priorities[index];
    if (priority === undefined) return [];
    return rowMenu(t, {
      name: priority.name,
      index,
      total: priorities.length,
      onMove: (to) => {
        priorityOrder.moveTo(priority.id, to);
      },
      isDefault: priority.isDefault,
      canDelete: priorities.length > 1,
      onEdit: () => {
        setEditing({ kind: 'priority', row: priority });
      },
      onDefault: () => {
        props.onDefaultPriority(priority.id);
      },
      onDelete: () => {
        setDeleting({
          kind: 'priority',
          id: priority.id,
          name: priority.name,
          count: priority.taskCount,
        });
      },
    });
  }

  /**
   * ⚠️⚠️ **NO WIDTH CAP — round 5 item 2.** Nine settings screens capped themselves at 680,
   * 760 or 980px, so moving between them moved the table under the reader. Ünnepnapok had
   * already lost its cap and is the shape the product owner named. The FORM-only screens keep
   * theirs: a field stretched across a wide monitor is a worse form, not a wider one.
   */
  const isStatuses = tab === 'statuses';

  const tabs = (
    <Tabs
      variant="underline"
      className="border-0"
      label={t('settings.nav.tasks')}
      value={tab}
      onValueChange={(value) => {
        setTab(
          value === 'priorities' ? 'priorities' : value === 'weights' ? 'weights' : 'statuses',
        );
      }}
      items={[
        {
          value: 'statuses',
          label: t('settings.statuses.title'),
          icon: 'list-checks',
          ...(props.loading === true ? {} : { count: statuses.length }),
        },
        {
          value: 'priorities',
          label: t('settings.priorities.title'),
          icon: 'flag',
          ...(props.loading === true ? {} : { count: priorities.length }),
        },
        { value: 'weights', label: t('settings.weights.title'), icon: 'sliders-horizontal' },
      ]}
    />
  );

  if (tab === 'weights') {
    return (
      <div data-screen="settings-tasks" className="flex min-h-0 flex-1 flex-col">
        <WeightsPanel tabs={tabs} canManage={canManage} />
      </div>
    );
  }

  return (
    <div data-screen="settings-tasks" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.tasks')}
        action={
          !canManage
            ? undefined
            : {
                label: t(isStatuses ? 'settings.statuses.new' : 'settings.priorities.new'),
                icon: 'plus',
                onSelect: () => {
                  setEditing({ kind: isStatuses ? 'status' : 'priority', row: null });
                },
              }
        }
        tabs={tabs}
        error={props.serverError}
        search={query}
        onSearch={setQuery}
        searchPlaceholder={t(
          isStatuses ? 'settings.statuses.search' : 'settings.priorities.search',
        )}
        filters={
          !isStatuses
            ? []
            : [
                {
                  key: 'category',
                  value: category,
                  ariaLabel: t('settings.statuses.column.category'),
                  options: [
                    { value: '', label: t('settings.statuses.allCategories') },
                    { value: 'open', label: t('status.category.open') },
                    { value: 'in_progress', label: t('status.category.inProgress') },
                    { value: 'closed', label: t('status.category.closed') },
                  ],
                  onChange: setCategory,
                },
              ]
        }
      >
        {/* ⚠️ The mockup's own grids: `26px minmax(0,1fr) 132px 126px 36px` for the statuses and
            one column narrower for the priorities, which carry no category. */}
        {isStatuses ? (
          <SettingsTable
            grid="26px minmax(0,1fr) 132px 126px 36px"
            minWidth={560}
            columns={[
              {},
              { label: t('settings.statuses.column.name') },
              { label: t('settings.statuses.column.category') },
              {},
              {},
            ]}
            groups={[{ rows: statusRows }]}
            loading={props.loading}
            empty={{
              icon: 'list-checks',
              title: t('settings.statuses.empty'),
              body: t('settings.statuses.emptyBody'),
            }}
          />
        ) : (
          <SettingsTable
            grid="26px minmax(0,1fr) 126px 36px"
            minWidth={520}
            columns={[{}, { label: t('settings.statuses.column.name') }, {}, {}]}
            groups={[{ rows: priorityRows }]}
            loading={props.loading}
            empty={{
              icon: 'flag',
              title: t('settings.priorities.empty'),
              body: t('settings.priorities.emptyBody'),
            }}
          />
        )}
      </SettingsPage>

      <EditDialog
        editing={editing}
        busy={saving}
        error={saveFailed ? props.serverError : null}
        onClose={() => {
          setSaveFailed(false);
          setEditing(null);
        }}
        onSubmit={(values) => {
          if (editing === null) return;
          let landing: Promise<boolean>;
          if (editing.kind === 'status') {
            const body = {
              name: values.name,
              color: values.color,
              category: values.category,
            };
            landing =
              editing.row === null
                ? props.onCreateStatus(body)
                : props.onUpdateStatus(editing.row.id, { ...body, version: editing.row.version });
          } else {
            const body = { name: values.name, color: values.color };
            landing =
              editing.row === null
                ? props.onCreatePriority(body)
                : props.onUpdatePriority(editing.row.id, { ...body, version: editing.row.version });
          }
          /* 26 Oct 2026 · closed only once it lands; a refusal stays, shown in the dialog. */
          setSaving(true);
          void landing.then((landed) => {
            setSaving(false);
            setSaveFailed(!landed);
            if (landed) setEditing(null);
          });
        }}
      />

      <DeleteDialog
        deleting={deleting}
        options={
          deleting?.kind === 'status'
            ? statuses
                .filter((row) => row.id !== deleting.id)
                .map((row) => ({ id: row.id, name: row.name }))
            : priorities
                .filter((row) => row.id !== deleting?.id)
                .map((row) => ({ id: row.id, name: row.name }))
        }
        onClose={() => {
          setDeleting(null);
        }}
        onConfirm={(reassignToId) => {
          if (deleting === null) return;
          if (deleting.kind === 'status') props.onDeleteStatus(deleting.id, reassignToId);
          else props.onDeletePriority(deleting.id, reassignToId);
          setDeleting(null);
        }}
      />
    </div>
  );
}

/**
 * The row menu, worded once for both lists.
 *
 * ⚠️ **"Make default" is absent on the row that already is one**, rather than disabled: it is not a
 * sequence anybody has to learn (unlike deactivate-before-delete), so an inert item would be noise.
 *
 * ⚠️ **Delete is disabled on the LAST row.** The service raises `LastOfItsKindError`; a status a task
 * could not be moved to is not a state the screen should be able to ask for.
 */
function rowMenu(
  t: Translate,
  options: {
    isDefault: boolean;
    canDelete: boolean;
    /** ⚠️ The row's own name, for the move labels — a menu item that says *"Feljebb"* and nothing
        else is announced identically on every row. */
    name: string;
    index: number;
    total: number;
    onEdit: () => void;
    onDefault: () => void;
    onMove: (to: number) => void;
    onDelete: () => void;
  },
): MenuItem[] {
  const items: MenuItem[] = [{ label: t('common.edit'), icon: 'pencil', onSelect: options.onEdit }];

  /**
   * ⚠️⚠️ **THE KEYBOARD PATH FOR THE GRABBER, AND IT IS NOT OPTIONAL.** No key event starts an
   * HTML5 drag, so a grip alone is a control a keyboard or screen-reader user cannot operate at
   * all. The chevrons this screen used to carry existed for that reason and were right about it;
   * what changed on 15 Sep is where the affordance lives, not whether everyone has one.
   */
  if (options.index > 0) {
    items.push({
      label: t('settings.moveUp', { name: options.name }),
      icon: 'chevron-up',
      onSelect: () => {
        options.onMove(options.index - 1);
      },
    });
  }
  if (options.index < options.total - 1) {
    items.push({
      label: t('settings.moveDown', { name: options.name }),
      icon: 'chevron-down',
      onSelect: () => {
        options.onMove(options.index + 1);
      },
    });
  }

  if (!options.isDefault) {
    items.push({ label: t('settings.makeDefault'), icon: 'check', onSelect: options.onDefault });
  }
  items.push({ type: 'separator' });
  items.push({
    label: t('common.delete'),
    icon: 'trash',
    destructive: true,
    disabled: !options.canDelete,
    onSelect: options.onDelete,
  });
  return items;
}

/**
 * ⚠️ A narrowing function rather than an `as` cast: a `<select>` hands back a plain string, and a
 * cast would let a future option name reach the server as an invalid enum.
 */
function asCategory(value: string): StatusCategory {
  return STATUS_CATEGORIES.includes(value as StatusCategory) ? (value as StatusCategory) : 'open';
}

/** The whole list with one item moved — what `reorder` wants. */
export function moved(ids: readonly string[], from: number, to: number): string[] {
  const next = [...ids];
  const [row] = next.splice(from, 1);
  if (row !== undefined) next.splice(to, 0, row);
  return next;
}

/* ── the dialogs ───────────────────────────────────────────────────────────────────────────── */

function EditDialog({
  editing,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  readonly editing: Editing;
  /* 26 Oct 2026 · the save in flight, and its refusal — the dialog stays open until it lands. */
  readonly busy: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (values: { name: string; color: string; category: StatusCategory }) => void;
}) {
  const t = useT();
  const isStatus = editing?.kind === 'status';
  const row = editing?.row ?? null;

  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(STATUS_COLORS[0]);
  const [category, setCategory] = useState<StatusCategory>('open');

  /* ⚠️ Re-seeded whenever a different row arrives, or the dialog shows the last one's values — the
   * same guard the onboarding wizard and the worklog entry dialog both carry. */
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const key = editing === null ? null : `${editing.kind}:${row?.id ?? 'new'}`;
  if (key !== null && seededFor !== key) {
    setSeededFor(key);
    setName(row?.name ?? '');
    setColor(row?.color ?? STATUS_COLORS[0]);
    /* ⚠️ `editing.kind` rather than `'category' in row`: `row` is a union of two shapes, and the
     * `in` narrowing does not survive the optional-chain that produced it. The discriminant does. */
    const status = editing?.kind === 'status' ? editing.row : null;
    setCategory(status?.category ?? 'open');
  }

  if (editing === null) return null;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={
        row === null
          ? isStatus
            ? t('settings.statuses.new')
            : t('settings.priorities.new')
          : t('common.edit')
      }
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('settings.field.name')}>
          <TextInput
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </Field>

        {/**
         * ⚠️⚠️ **NO PRESETS — item 22, second pass.** The eight status colours survived the first
         * pass as a swatch grid with the wheel behind a button, which is the arrangement the
         * feedback asked to be rid of. `STATUS_COLORS[0]` still seeds a NEW status, so a row
         * created without
         * touching this control is one of the product's own colours; it is no longer a menu.
         */}
        <ColorPicker label={t('settings.field.color')} value={color} onChange={setColor} />

        {isStatus && (
          <Field label={t('settings.field.category')}>
            <Select
              value={category}
              onChange={(event) => {
                setCategory(asCategory(event.target.value));
              }}
              options={STATUS_CATEGORIES.map((value) => ({
                value,
                label: categoryLabel(t, value),
              }))}
            />
          </Field>
        )}

        {error === null ? null : (
          <p role="alert" className="text-destructive text-[12px]">
            {error}
          </p>
        )}
        <div className="flex items-center gap-2">
          <div className="flex-1" />
          <Button variant="outline" disabled={busy} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={name.trim() === '' || busy}
            onClick={() => {
              onSubmit({ name: name.trim(), color, category });
            }}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * ⚠️⚠️ **DELETING ASKS WHERE THE TASKS GO, because the service requires it.** The FK is `restrict`,
 * so a delete without a target fails with a constraint name; the mockup's own dialog picks a target
 * and states the count, and this does the same.
 */
function DeleteDialog({
  deleting,
  options,
  onClose,
  onConfirm,
}: {
  readonly deleting: Deleting;
  readonly options: readonly { id: string; name: string }[];
  readonly onClose: () => void;
  readonly onConfirm: (reassignToId: string) => void;
}) {
  const t = useT();
  const [target, setTarget] = useState('');

  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (deleting !== null && seededFor !== deleting.id) {
    setSeededFor(deleting.id);
    setTarget(options[0]?.id ?? '');
  }

  if (deleting === null) return null;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('settings.delete.rowTitle', { name: deleting.name })}
      description={t('settings.delete.rowBody', { count: String(deleting.count) })}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('settings.delete.reassign')}>
          <Select
            value={target}
            onChange={(event) => {
              setTarget(event.target.value);
            }}
            options={options.map((option) => ({ value: option.id, label: option.name }))}
          />
        </Field>

        <div className="flex items-center gap-2">
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {/* ⚠️ A neutral OUTLINE, not the filled danger button — the kit reserves that for
              irreversible financial deletions, and this moves tasks rather than losing them. */}
          <Button
            variant="outline"
            className="text-destructive"
            disabled={target === ''}
            onClick={() => {
              onConfirm(target);
            }}
          >
            {t('common.delete')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
